import { db, auth } from './config';
import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  setDoc, 
  updateDoc, 
  deleteDoc,
  query,
  orderBy 
} from 'firebase/firestore';
import { 
  createUserWithEmailAndPassword,
  deleteUser as deleteAuthUser
} from 'firebase/auth';

// الحصول على جميع المستخدمين
export const getAllUsers = async () => {
  try {
    const usersRef = collection(db, 'users');
    const q = query(usersRef, orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    
    const users = [];
    snapshot.forEach((doc) => {
      users.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return users;
  } catch (error) {
    console.error('Error getting users:', error);
    throw error;
  }
};

// الحصول على معلومات مستخدم واحد
export const getUserById = async (userId) => {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (userDoc.exists()) {
      return {
        id: userDoc.id,
        ...userDoc.data()
      };
    }
    return null;
  } catch (error) {
    console.error('Error getting user:', error);
    throw error;
  }
};

// الحصول على معلومات مستخدم بالبريد الإلكتروني
export const getUserByEmail = async (email) => {
  try {
    const usersRef = collection(db, 'users');
    const snapshot = await getDocs(usersRef);
    
    let user = null;
    snapshot.forEach((doc) => {
      if (doc.data().email === email) {
        user = {
          id: doc.id,
          ...doc.data()
        };
      }
    });
    
    return user;
  } catch (error) {
    console.error('Error getting user by email:', error);
    throw error;
  }
};

// إضافة مستخدم جديد
export const addUser = async (userData) => {
  try {
    // حفظ بيانات المدير الحالي قبل إنشاء المستخدم الجديد
    const currentAdmin = auth.currentUser;
    if (!currentAdmin) {
      throw new Error('يجب تسجيل الدخول كمدير أولاً');
    }
    
    // حفظ token المدير
    const adminToken = await currentAdmin.getIdToken();
    const adminEmail = currentAdmin.email;
    
    // إنشاء المستخدم في Firebase Authentication
    const userCredential = await createUserWithEmailAndPassword(
      auth,
      userData.email,
      userData.password
    );
    
    const newUser = userCredential.user;
    
    // حفظ معلومات المستخدم في Firestore
    const userRef = doc(db, 'users', newUser.uid);
    await setDoc(userRef, {
      email: userData.email,
      displayName: userData.displayName,
      role: userData.role || 'editor',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      active: true
    });
    
    // تسجيل خروج المستخدم الجديد فوراً
    await auth.signOut();
    
    // إعادة تسجيل دخول المدير باستخدام token
    try {
      const { signInWithCustomToken } = await import('firebase/auth');
      // محاولة استخدام token
      await auth.updateCurrentUser(currentAdmin);
    } catch (tokenError) {
      console.log('Token restore failed, user needs to refresh');
    }
    
    return {
      id: newUser.uid,
      email: userData.email,
      displayName: userData.displayName,
      role: userData.role || 'editor',
      requiresRefresh: true // علامة لإعادة التحميل
    };
  } catch (error) {
    console.error('Error adding user:', error);
    
    // رسائل خطأ مخصصة بالعربية
    if (error.code === 'auth/email-already-in-use') {
      throw new Error('البريد الإلكتروني مستخدم بالفعل');
    } else if (error.code === 'auth/invalid-email') {
      throw new Error('البريد الإلكتروني غير صحيح');
    } else if (error.code === 'auth/weak-password') {
      throw new Error('كلمة المرور ضعيفة جداً');
    }
    
    throw error;
  }
};

// تحديث صلاحيات المستخدم
export const updateUserRole = async (userId, newRole) => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      role: newRole,
      updatedAt: new Date().toISOString()
    });
    
    return true;
  } catch (error) {
    console.error('Error updating user role:', error);
    throw error;
  }
};

// تحديث بيانات مستخدم
export const updateUser = async (userId, userData) => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      ...userData,
      updatedAt: new Date().toISOString()
    });
    
    return true;
  } catch (error) {
    console.error('Error updating user:', error);
    throw error;
  }
};

// تبديل حالة المستخدم (تفعيل/تعطيل)
export const toggleUserStatus = async (userId, currentStatus) => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      active: !currentStatus,
      updatedAt: new Date().toISOString()
    });
    
    return !currentStatus;
  } catch (error) {
    console.error('Error toggling user status:', error);
    throw error;
  }
};

// تحديث كامل لبيانات المستخدم (البريد، كلمة المرور، الصلاحيات)
export const updateUserComplete = async (userId, userData) => {
  try {
    const { email, displayName, role, newPassword } = userData;
    
    // الحصول على البيانات القديمة
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    const oldEmail = userDoc.data()?.email;
    
    // تحديث البيانات في Firestore
    const updateData = {
      displayName,
      role,
      updatedAt: new Date().toISOString()
    };
    
    // إذا تم تغيير البريد الإلكتروني
    if (email && email !== oldEmail) {
      updateData.email = email;
      updateData.emailChanged = true;
      updateData.oldEmail = oldEmail;
    }
    
    // إذا تم تغيير كلمة المرور
    if (newPassword) {
      updateData.passwordChanged = true;
    }
    
    await updateDoc(userRef, updateData);
    
    // محاولة تحديث Firebase Authentication مباشرة
    // ملاحظة: هذا يعمل فقط إذا كان المستخدم الحالي هو المدير
    // وقد قام بتسجيل الدخول مؤخراً
    try {
      const { getFunctions, httpsCallable } = await import('firebase/functions');
      const functions = getFunctions();
      
      // استدعاء Cloud Function لتحديث المستخدم
      const updateUserAuth = httpsCallable(functions, 'updateUserAuth');
      await updateUserAuth({
        userId,
        email: email !== oldEmail ? email : null,
        password: newPassword || null
      });
    } catch (authError) {
      console.log('Cloud Function not available, changes saved to Firestore only');
      // التغييرات محفوظة في Firestore على الأقل
    }
    
    return true;
  } catch (error) {
    console.error('Error updating user complete:', error);
    
    if (error.code === 'auth/email-already-in-use') {
      throw new Error('البريد الإلكتروني مستخدم بالفعل');
    } else if (error.code === 'auth/invalid-email') {
      throw new Error('البريد الإلكتروني غير صحيح');
    }
    
    throw error;
  }
};

// حذف مستخدم
export const deleteUser = async (userId) => {
  try {
    // حذف من Firestore
    const userRef = doc(db, 'users', userId);
    await deleteDoc(userRef);
    
    // محاولة حذف المستخدم من Authentication عبر Cloud Function
    try {
      const { getFunctions, httpsCallable } = await import('firebase/functions');
      const functions = getFunctions();
      const deleteUserAuth = httpsCallable(functions, 'deleteUserAuth');
      
      await deleteUserAuth({ userId });
      console.log('User deleted from Authentication successfully');
    } catch (authError) {
      console.log('Cloud Function not available for Authentication deletion');
      // المستخدم محذوف من Firestore على الأقل
    }
    
    return true;
  } catch (error) {
    console.error('Error deleting user:', error);
    throw error;
  }
};

// التحقق من صلاحيات المستخدم
export const checkUserPermission = async (userId, requiredRole) => {
  try {
    const user = await getUserById(userId);
    
    if (!user) return false;
    
    const roleHierarchy = {
      admin: 3,
      editor: 2,
      viewer: 1
    };
    
    const userLevel = roleHierarchy[user.role] || 0;
    const requiredLevel = roleHierarchy[requiredRole] || 0;
    
    return userLevel >= requiredLevel;
  } catch (error) {
    console.error('Error checking permission:', error);
    return false;
  }
};

// الحصول على صلاحيات المستخدم
export const getUserPermissions = (role) => {
  const permissions = {
    admin: {
      canManageUsers: true,
      canManageImages: true,
      canManageCategories: true,
      canViewStats: true,
      canManageContact: true,
      canManageAbout: true,
      canViewMessages: true
    },
    editor: {
      canManageUsers: false,
      canManageImages: true,
      canManageCategories: true,
      canViewStats: true,
      canManageContact: true,
      canManageAbout: true,
      canViewMessages: true
    },
    viewer: {
      canManageUsers: false,
      canManageImages: false,
      canManageCategories: false,
      canViewStats: true,
      canManageContact: false,
      canManageAbout: false,
      canViewMessages: false
    }
  };
  
  return permissions[role] || permissions.viewer;
};

// تسجيل نشاط المستخدم
export const logUserActivity = async (userId, action, details = {}) => {
  try {
    const activityRef = collection(db, 'userActivity');
    await setDoc(doc(activityRef), {
      userId,
      action,
      details,
      timestamp: new Date().toISOString()
    });
  } catch (error) {
    console.error('Error logging activity:', error);
  }
};
